import { useGoogleRecaptchaV2 } from '@sgcmpteltd/uikit-next';
import {
  AutoResizeTextArea,
  ErrorMessage,
  Input,
} from '@sgcmpteltd/uikit-react';
import {
  Button,
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_CONTAINED,
} from '@sgcmpteltd/uikit-react/components/button';
import { useRef, useState, useTransition } from 'react';

import { TextLink } from '@/components/text-link';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/configs/google/recaptcha';
import { useValidation } from '@/hooks/use-validation';

import { RecaptchaBrandingText } from '../../recaptcha-branding-text';
import type { OnGoogleRecaptchSuccess } from '../types';
import {
  ERROR_MESSAGE_EMAIL_MESSAGE,
  ERROR_MESSAGE_RECIPIENTS_EMAIL,
  ERROR_MESSAGE_SENDER_NAME,
  RECIPEINTS_EMAIL_SEPARATOR,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';
import { promotionUrlShareContentSchema } from './validation';

export const Step: Component = ({
  link,
  onSend,
}) => {
  const googleRecaptchaWidgetElRef = useRef<HTMLDivElement | null>(
    null,
  );
  const onGoogleRecaptchSuccessRef = useRef<OnGoogleRecaptchSuccess | null>(
    null,
  );

  const [senderName, setSenderName] = useState('');
  const [
    recipientsJoinedEmailList,
    setRecipientsJoinedEmailList,
  ] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isRecaptchaPending, setIsRecaptchaPending] = useState(false);
  const [
    isSendShareEmailPending,
    startSendShareEmailTransition,
  ] = useTransition();

  const {
    errorMessageMap,
    validate,
  } = useValidation({
    schema: promotionUrlShareContentSchema,
  });
  const {
    senderName: senderNameErrorMessage,
    recipientsJoinedEmailList: recipientsJoinedEmailListErrorMessage,
    emailMessage: emailMessageErrorMessage,
  } = errorMessageMap ?? {};

  const {
    reset: resetRecaptcha,
    execute: executeRecaptcha,
  } = useGoogleRecaptchaV2({
    siteKey: GOOGLE_RECAPTCHA_SITE_KEY ?? '',
    widgetElRef: googleRecaptchaWidgetElRef,
    optionsRef: useRef({
      size: 'invisible',
    }),
    onSuccess: () => {
      setIsRecaptchaPending(false);

      onGoogleRecaptchSuccessRef.current?.();
    },
    onExpired: () => {
      setIsRecaptchaPending(false);
    },
    onError: () => {
      setIsRecaptchaPending(false);
    },
  });

  return (
    <div
      className={styles.containerRoot}>
      <span
        className={styles.textStepTitle}>
        EMAIL THIS PAGE
      </span>
      <div
        className={styles.containerStepForm}>
        <span
          className={styles.containerLinkSendTarget}>
          The following url will be sent to your friend:
          <div>
            <TextLink
              href={link}
              text={link} />
          </div>
        </span>
        <div
          className={styles.containerStepFormFields}>
          <div
            className={styles.containerFormField}>
            <Input
              isInvalid={!!senderNameErrorMessage}
              placeholder="Your Name*"
              value={senderName}
              onBlur={() => {
                setSenderName(previousSenderName => (
                  previousSenderName.trim()
                ));
              }}
              onChange={({
                target: {
                  value: newSenderName,
                },
              }) => {
                setSenderName(newSenderName);
              }} />
            {
              senderNameErrorMessage && (
                <span
                  className={styles.textFormFieldErrorMessage}>
                  <ErrorMessage>
                    {ERROR_MESSAGE_SENDER_NAME}
                  </ErrorMessage>
                </span>
              )
            }
          </div>
          <div
            className={styles.containerFormField}>
            <AutoResizeTextArea
              isInvalid={!!recipientsJoinedEmailListErrorMessage}
              placeholder="Recipient's email* (one per line)"
              rows={3}
              value={recipientsJoinedEmailList}
              onBlur={() => {
                setRecipientsJoinedEmailList(
                  previousRecipientsJoinedEmailList => (
                    previousRecipientsJoinedEmailList.trim()
                  ),
                );
              }}
              onChange={({
                target: {
                  value: newRecipientsJoinedEmailList,
                },
              }) => {
                setRecipientsJoinedEmailList(newRecipientsJoinedEmailList);
              }} />
            {
              recipientsJoinedEmailListErrorMessage && (
                <span
                  className={styles.textFormFieldErrorMessage}>
                  <ErrorMessage>
                    {ERROR_MESSAGE_RECIPIENTS_EMAIL}
                  </ErrorMessage>
                </span>
              )
            }
            <span
              className={styles.textRecipientEmailNotice}>
              Please use commas to separate multiple email addresses.
              Maximum 6 email addresses allowed.
            </span>
          </div>
          <div
            className={styles.containerFormField}>
            <AutoResizeTextArea
              isInvalid={!!emailMessageErrorMessage}
              placeholder="Message"
              rows={3}
              value={emailMessage}
              onBlur={() => {
                setEmailMessage(previousEmailMessage => (
                  previousEmailMessage.trim()
                ));
              }}
              onChange={({
                target: {
                  value: newEmailMessage,
                },
              }) => {
                setEmailMessage(newEmailMessage);
              }} />
            {
              emailMessageErrorMessage && (
                <span
                  className={styles.emailMessageErrorMessage}>
                  <ErrorMessage>
                    {ERROR_MESSAGE_EMAIL_MESSAGE}
                  </ErrorMessage>
                </span>
              )
            }
          </div>
        </div>
        <div ref={googleRecaptchaWidgetElRef} />
      </div>
      <div className={styles.containerFormField}>
        <div
          className={styles.containerSendButton}>
          <Button
            color={BUTTON_COLOR_RED}
            disabled={isRecaptchaPending || isSendShareEmailPending}
            variant={BUTTON_VARIANT_CONTAINED}
            onClick={() => {
              if (!onSend) {
                return;
              }

              const validationResult = validate({
                senderName,
                recipientsJoinedEmailList,
                emailMessage,
              });

              if (!validationResult.success) {
                return;
              }

              const {
                output: {
                  senderName: outputSenderName,
                  recipientsJoinedEmailList: outputRecipientsJoinedEmailList,
                  emailMessage: outputEmailMessage,
                },
              } = validationResult;

              onGoogleRecaptchSuccessRef.current = () => {
                startSendShareEmailTransition(async () => {
                  await onSend({
                    senderName: outputSenderName,
                    recipientsJoinedEmailList: Array.from(
                      new Set(
                        outputRecipientsJoinedEmailList.split(
                          RECIPEINTS_EMAIL_SEPARATOR,
                        ),
                      ),
                    ).join(','),
                    emailMessage: outputEmailMessage,
                  });
                });
              };

              resetRecaptcha();
              executeRecaptcha();
              setIsRecaptchaPending(true);
            }}>
            Send
          </Button>
        </div>
        <div
          className={styles.containerRecaptchaBrandingText}>
          <RecaptchaBrandingText />
        </div>
      </div>
    </div>
  );
};
